import React, { useCallback, useEffect, useState} from "react";
import {
  Typography,
  Box,
  TextField,
  Autocomplete,
  Tabs,
  Tab,
  Button,
  Modal,
  IconButton,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useHistory, useLocation } from "react-router";
import { ROUTE_PATHS } from "src/routes";
import { FuelCatalogDetailTab } from "./catalog_detail_tab/FuelCatalogDetailTab";
import { getFuelCatalogs } from "src/apis/getCatalogCalls";
import { useStoreContext } from "src/store/context";
import { useAddAlert } from "src/store/actions";
import { alertMessages } from "src/constants";
import { DeleteCatalog } from "../modals/DeleteCatalog";
import { checkAdminRole } from "src/store/common";
import StationAssignment from "./catalog_stations/StationAssignment";
import { deleteModalStyle } from "src/shared/Styles/Styles";
import { AuditCatalog } from "./catalog_audits/AuditCatalog";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box >{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function FuelModelCatalogDetail({tab}) {
  const history = useHistory();
  const location = useLocation();
  const [state, dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);

  const [tabValue, setTabValue] = useState(tab);
  const [fuelModelCatalog, setFuelModelCatalog] = useState<any>(null);
  const [routes, setRoutes ] = useState<string[]>([]);
  const [searchedFMCatalog, setSearchedFMCatalog] = useState<any>(null);
  const [searchOptions, setSearchOptions] = useState<any[]>([])
  const [editFuel, setEditFuel] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deleteFuelCatalogSuccess, setDeleteFuelCatalogSuccess] = useState<boolean>(false);

  /** Make the api call for the fuel catalog detail */
  const fuelCatalogCallback = useCallback((request: any, search?:boolean) => {
    (async () => {
      try {
        const fuelCatalogTable: any = await getFuelCatalogs(request, addAlert);
        if (fuelCatalogTable.data.length > 0) {
          if(search){
            setSearchOptions(fuelCatalogTable.data)
          }
          else{
            setFuelModelCatalog(fuelCatalogTable.data[0]);
            setEditFuel(fuelCatalogTable.data[0]);
          }
        } else {
          search? setSearchOptions([]):setFuelModelCatalog(null);
        }

      } catch (error) {
        search? setSearchOptions([]):setFuelModelCatalog(null);
        addAlert({
          id: '',
          message: alertMessages.retrieveCatalog,
          type: 'error-info',
          icon: 'fa-exclamation-triangle'
        });
      }
    })();
  }, [addAlert]);

  const clickViewAllFuelModelCatalogs = useCallback(()=>{
    history.push({
      pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}`,
      state: {...fuelModelCatalog, page: 0, perPage: 10}
    })
  }, [history, fuelModelCatalog]);

  useEffect(()=>{
    if(location?.state){
      const thisFuelModelCatalog:any = location.state;
      if(thisFuelModelCatalog.from){
        setSearchedFMCatalog(thisFuelModelCatalog)
      }
      setFuelModelCatalog(thisFuelModelCatalog);
      setEditFuel(thisFuelModelCatalog);
      setRoutes([`${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/detail/${thisFuelModelCatalog?.catalogId}`, 
      `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/stationAssign/${thisFuelModelCatalog?.catalogId}`,
      `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/audit/${thisFuelModelCatalog?.catalogId}`,
      ])
    }
    else{
      const stringArray = decodeURIComponent(location.pathname).split("/");
      const catalogId = stringArray[stringArray?.length-1];
      const request = {
        id: Number(catalogId),
        catalogName: null,
        perPage: 10000,
        page: 0,
        sortBy: 'fire_danger_model_name',
        sortOrder: 'asc',
        fuelModel: 'ALL'
      }
      fuelCatalogCallback(request);
      setRoutes([`${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/detail/${catalogId}`, 
      `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/stationAssign/${catalogId}`,
      `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/audit/${catalogId}`,
      ])
    }
  },[location?.pathname, location.state, fuelCatalogCallback]);

  /*successful save or update or delete*/
    useEffect(() => {
      if (deleteFuelCatalogSuccess) {
        clickViewAllFuelModelCatalogs();
      }
    }, [ deleteFuelCatalogSuccess, clickViewAllFuelModelCatalogs]);

  /* call to get search options*/
  useEffect(()=>{
      const requestSearch = {
        id: null,
        catalogName: null,
        perPage: 10000,
        page: 0,
        sortBy: 'fire_danger_model_name',
        sortOrder: 'asc',
        fuelModel: 'ALL'
      }
    fuelCatalogCallback(requestSearch, true)
  },[fuelCatalogCallback]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    history.push({
      pathname: `${routes[newValue]}`,
      state: {...fuelModelCatalog}
    });
  };

  const onFuelModelCatalogSearch = (e:React.SyntheticEvent<Element, Event>, incomingVal: any) =>{
    history.push(
      { pathname: `${ROUTE_PATHS.table}${ROUTE_PATHS.fuelTab}/detail/${incomingVal?.catalogId}`, state: { ...incomingVal, from: 'search' } }
    )
  }

  const handleDeleteCatalog = () => {
    setOpenDeleteModal(true);
  }
  
  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  }

  return (
    <Box>
      <Modal //Modal to delete catalog
        className="smooth-scrollbar"
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
      >
        <div style={{ height: '100%' }}>
          <IconButton aria-label='close' style={{ left: '64%', top:'23.5%', position: 'absolute', cursor: 'pointer' }} onClick={(e)=>handleCloseDeleteModal()} >
            <CloseIcon style={{ color: 'white' }} />
          </IconButton>
          <Box sx={deleteModalStyle} className="smooth-scrollbar removePadding" >
            { state.user && checkAdminRole(state.user) &&
              <DeleteCatalog setOpenDeleteModal={setOpenDeleteModal} setDeleteFuelCatalogSuccess={setDeleteFuelCatalogSuccess} deleteCatalogId={fuelModelCatalog?.catalogId} catalogName={fuelModelCatalog?.fireDangerModelName} catalogCategory={'Parent'}/>
            }
          </Box>
        </div>
      </Modal>
      {/* Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          padding: 2,
        }}
      >
        {/* Left Section */}
        <Box sx={{display: 'flex', flexDirection:'column'}}>
          <Typography variant="h5">
            <span style={{marginRight: '1rem', textTransform:'capitalize'}}>{fuelModelCatalog?.fireDangerModelName}</span> 
            <Button variant="contained" style={{marginLeft:'1rem'}} 
                onClick={()=>{history.push(
                  {
                    pathname: `${ROUTE_PATHS.fuelCreateUpdate}/${editFuel?.catalogId}/${editFuel?.catalogName}`,
                    state: {...editFuel, tab: tabValue},
                  }
                )}}>Edit Catalog</Button>
            { !(fuelModelCatalog?.catalogId >= 1 && fuelModelCatalog?.catalogId <= 5) &&
              <Button variant="contained" color='error' style={{marginLeft:'1rem'}} onClick={()=>handleDeleteCatalog()}>Delete Catalog</Button>
            }
          </Typography>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: "flex", flexDirection: "column", marginRight: '1rem',alignItems: "flex-end", width: '25vw' }}>
          <div className="searchStationsDownloadPage">
            {/* Search Icons */}
            <i className="fas fa-search"/>
            <Autocomplete
              id="fuelModelDetailCatalogSearch"
              clearOnEscape
              freeSolo
              noOptionsText={
                <div className='no-results'>
                    <div>No results match your search criteria.</div>
                </div>
              }
              options={searchOptions}
              getOptionLabel={(option)=>`${option.fireDangerModelName}`}           
              aria-label="fuelModelDetailCatalogSearch"
              value={searchedFMCatalog}
              onChange={(event, newValue) => onFuelModelCatalogSearch(event, newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={'Search Fuel Model Catalogs...'}
                  placeholder="Search Fuel Model Catalogs..."
                />
              )}
            />
          </div>
          <Box mt={1}>
            <Button variant='text' onClick={clickViewAllFuelModelCatalogs} className="font-sans-sm text-bold" style={{ color: "#1675e0", textTransform:'capitalize' }}>
              View All Fuel Model Catalogs
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Tabs Section */}
      <Box sx={{ width: "100%", marginTop: 3 }}>
        <Box>
        {/* Tabs Section */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="custom tabs example"
            className="tabcatalog"
          >
            <Tab label="Details" {...a11yProps(0)} />
            <Tab label="Catalog's Stations" {...a11yProps(1)} />
            <Tab label="Audit Logs" {...a11yProps(2)} />

          </Tabs>
        </Box>
        {/* Tab Panels */}
        <CustomTabPanel value={tabValue} index={0}>
          <div style={{backgroundColor: '#dddddd', height: '60vh'}}>
            <FuelCatalogDetailTab fuelCatalog={fuelModelCatalog}/>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={1}>
          <div style={{backgroundColor: '#eeeeee', height: '100%'}}>
            <StationAssignment fuelModelCatalog={fuelModelCatalog}/>
          </div>
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={2}>
          <div style={{backgroundColor: '#eeeeee', height: '100%'}}>
            <AuditCatalog catalogType={'FuelModel'}/>
          </div>
        </CustomTabPanel>
      </Box>

    </Box>
  );
}

export default FuelModelCatalogDetail;